<template>
  <v-card>
    <DialogBaseColoredTitle color="primary">
      {{ $t('page.registerForm.header') }}
    </DialogBaseColoredTitle>
    <DialogBaseCloseIcon color="white" @click="$emit('close')" />
    <v-divider></v-divider>
    <DialogBaseText class="text-center mt-4">
      <div class="mb-5">
        <v-icon size="20">mdi-information-outline</v-icon>
        <span v-html="$t('page.registerForm.terms.of.use')"></span>
      </div>
      <v-text-field
        id="email"
        v-model="email"
        class="cms_eMail"
        :label="$t('page.registerForm.email.text')"
        :error-messages="emailErrors"
        outlined
        dense
        required
        @input="$v.email.$touch()"
        @keyup.enter.native="submit()"
      ></v-text-field>
      <v-text-field
        id="password"
        v-model="pwd"
        class="cms_pw"
        :label="$t('page.registerForm.password.text')"
        :error-messages="pwdErrors"
        :type="showPwd ? 'text' : 'password'"
        :append-icon="showPwd ? 'mdi-eye' : 'mdi-eye-off'"
        counter
        outlined
        dense
        required
        @input="$v.pwd.$touch()"
        @keyup.enter.native="submit()"
        @click:append="showPwd = !showPwd"
      ></v-text-field>
      <v-text-field
        id="repeatPassword"
        v-model="repeatPwd"
        :label="$t('page.registerForm.repeat.password.text')"
        :error-messages="repeatPwdErrors"
        :type="showRepeatPwd ? 'text' : 'password'"
        :append-icon="showRepeatPwd ? 'mdi-eye' : 'mdi-eye-off'"
        counter
        outlined
        dense
        required
        @input="$v.repeatPwd.$touch()"
        @keyup.enter.native="submit()"
        @click:append="showRepeatPwd = !showRepeatPwd"
      ></v-text-field>
      <v-checkbox
        id="marketing"
        v-model="subscribeMarketing"
        :label="$t('page.registerForm.marketing.mails.checkbox')"
        outlined
        dense
      ></v-checkbox>
      <v-btn
        id="routeToLogin"
        :to="{ name: 'login', params: { locale: locale } }"
        color="primary"
        class="mt-3"
        text
        outlined>
        {{ $t('button.already.registered') }}
      </v-btn>
    </DialogBaseText>
    <v-divider></v-divider>
    <DialogBaseActions>
      <v-row class="ma-0">
        <v-col>
          <v-btn
            id="register"
            class="text-center px-8 cms_register"
            color="primary"
            dark
            depressed
            :loading="loading"
            @click.stop="submit"
            >{{ $t('button.register') }}</v-btn>
          <router-view></router-view>
        </v-col>
      </v-row>
    </DialogBaseActions>
  </v-card>
</template>
<script>
import { validationMixin } from 'vuelidate'
import { fieldValidationsMixin } from '@/mixins/fieldValidationsMixin'
import { required, email, minLength, sameAs } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'
import registerFormJson from '@/data/json/homepage/registerForm.json'
import errorsJson from '@/data/json/errors/errors.json'
import validationsJson from '@/data/json/errors/validations.json'

export default {
  mixins: [validationMixin, fieldValidationsMixin],
  validations: {
    email: { required, email },
    pwd: {
      required,
      minLength: minLength(6),
      hasCapital(pwd) {
        return /[A-Z]/.test(pwd)
      },
      hasNum(pwd) {
        return /[0-9]/.test(pwd)
      },
      hasSpecialChar(pwd) {
        return /\W|_/.test(pwd)
      }
    },
    repeatPwd: {
      required,
      sameAsPassword: sameAs('pwd'),
      minLength: minLength(6)
    }
  },
  data: () => ({
    content: registerFormJson.data.concat(
      errorsJson.data,
      validationsJson.data
    ),
    email: '',
    pwd: '',
    repeatPwd: '',
    subscribeMarketing: false,
    showPwd: false,
    showRepeatPwd: false,
    loading: false
  }),
  computed: {
    ...mapGetters(['locale']),
    emailErrors() {
      const errors = []
      if (!this.$v.email.$dirty) return errors
      !this.$v.email.email &&
        errors.push(this.$t('validation.email.invalid'))
      !this.$v.email.required &&
        errors.push(this.$t('validation.email.required'))
      return errors
    },
    pwdErrors() {
      const errors = []
      if (!this.$v.pwd.$dirty) return errors
      !this.$v.pwd.required &&
        errors.push(this.$t('validation.password.required'))
      !this.$v.pwd.minLength &&
        errors.push(this.$t('validation.password.min.length'))
      !this.$v.pwd.hasCapital &&
        errors.push(
          this.$t('validation.password.capital.letter.required')
        )
      !this.$v.pwd.hasNum &&
        errors.push(this.$t('validation.password.number.required'))
      !this.$v.pwd.hasSpecialChar &&
        errors.push(
          this.$t('validation.password.special.char.required')
        )
      return errors
    },
    repeatPwdErrors() {
      const errors = []
      if (!this.$v.repeatPwd.$dirty) return errors
      !this.$v.repeatPwd.required &&
        errors.push(
          this.$t('validation.password.repeat.required')
        )
      !this.$v.repeatPwd.minLength &&
        errors.push(
          this.$t('validation.password.repeat.min.length')
        )
      !this.$v.repeatPwd.sameAsPassword &&
        errors.push(this.$t('validation.passwords.not.identical'))
      return errors
    }
  },
  methods: {
    submit() {
      let locale = this.locale
      this.$v.$touch()
      this.email = this.email.trim()
      if (this.$v.$invalid) {
        this.$notify(this.$t('validation.form.incomplete'), {
          type: 'error'
        })
        return
      }
      this.loading = true
      this.$userService
        .register({
          email: this.email,
          password: this.pwd,
          confirmPassword: this.repeatPwd,
          MarketingMails: this.subscribeMarketing,
          confirmationURL: `${window.config.VUE_APP_BASE_URL}/registrationconfirmation`
        })
        .then(() => {
          this.$notify(this.$t('notification.registration.success'), {
            type: 'success'
          })
          this.$router.push({
            name: 'thankYou',
            params: { locale: locale, email: this.email }
          })
        })
        .catch(e => {
          const msg = e.fromCms
            ? e.message
            : this.$t(
              e.response.status === 409
                ? 'component.profile.accountManagement.email.taken'
                : e.response.status
            )
          this.$notify(msg, { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
<style lang="scss">
.avv_checkbox {
  .v-input--selection-controls__input {
    position: absolute;
    top: 0;
  }
}
.avv_style {
  font-size: 12px;
  font-weight: 600;
}
</style>